import { useRef, useLayoutEffect } from 'react';

/**
 * Type for cursor update information
 */
export type CursorUpdate = {
  element: HTMLInputElement;
  position: number;
  active: boolean;
};

/**
 * Counts the number of digits in a string before a specific cursor position
 */
export const countDigitsBeforeCursor = (
  text: string,
  cursorPos: number
): number => {
  let digitCount = 0;
  for (let i = 0; i < Math.min(cursorPos, text.length); i++) {
    if (/\d/.test(text[i])) {
      digitCount++;
    }
  }
  return digitCount;
};

/**
 * Calculates the new cursor position in a formatted phone number.
 * @param formattedNumber The fully formatted phone number string.
 * @param targetDigitCount The number of digits that should precede the cursor.
 * @param formattedDialCode The visual representation of the dial code (e.g., "+61").
 * @param dialCodeDigitsOnly The numeric-only digits of the dial code (e.g., "61").
 */
export const calculateCursorPosition = (
  formattedNumber: string,
  targetDigitCount: number,
  formattedDialCode: string, // e.g. "+61" or "+1 684"
  dialCodeDigitsOnly: string // e.g. "61" or "1684"
): number => {
  let newCursorPos = 0;
  let currentDigitCount = 0;

  // Find position after the target number of digits
  for (let i = 0; i < formattedNumber.length; i++) {
    if (/\d/.test(formattedNumber[i])) {
      currentDigitCount++;
      if (currentDigitCount > targetDigitCount) {
        break;
      }
    }
    newCursorPos = i + 1; // Position after current character
  }

  // Ensure cursor is at least at the end of the visual dial code.
  // This prevents cursor from being placed inside the dial code characters.
  const minCursorPosAfterDialCode = formattedDialCode.length;

  // Additional check: if dialCodeDigitsOnly has content, ensure cursor is after its last digit's occurrence.
  // This handles cases like "+1 684" where formattedDialCode.length is sufficient.
  let minPosBasedOnDigits = minCursorPosAfterDialCode;
  if (dialCodeDigitsOnly.length > 0) {
    const lastDialDigit = dialCodeDigitsOnly.charAt(
      dialCodeDigitsOnly.length - 1
    );
    const indexOfLastDialDigit = formattedNumber.indexOf(lastDialDigit);
    if (indexOfLastDialDigit !== -1) {
      // Ensure this index is within the expected dial code part
      let tempDigitsCount = 0;
      let k = 0;
      for (; k <= indexOfLastDialDigit; k++) {
        if (/\d/.test(formattedNumber[k])) {
          tempDigitsCount++;
        }
      }
      if (tempDigitsCount <= dialCodeDigitsOnly.length) {
        minPosBasedOnDigits = indexOfLastDialDigit + 1;
      }
    }
  }

  const effectiveMinPos = Math.max(
    minCursorPosAfterDialCode,
    minPosBasedOnDigits
  );

  return Math.max(newCursorPos, effectiveMinPos);
};

/**
 * Hook for managing cursor position in input elements
 */
export const useCursorPosition = () => {
  // Ref to store cursor position data for updates
  const cursorUpdateRef = useRef<CursorUpdate | null>(null);
  const cursorPositionRef = useRef<number | null>(null);

  // Apply cursor position update after render
  useLayoutEffect(() => {
    if (!cursorUpdateRef.current?.active) {
      return;
    }

    const { element, position } = cursorUpdateRef.current;

    if (document.activeElement === element) {
      element.setSelectionRange(position, position);
    }

    cursorUpdateRef.current.active = false;
  });

  /**
   * Schedule a cursor position update for after the next render
   */
  const scheduleCursorUpdate = (
    element: HTMLInputElement | null | undefined,
    position: number
  ) => {
    if (!element) {
      return;
    }

    cursorUpdateRef.current = {
      element,
      position,
      active: true,
    };
  };

  /**
   * Store current cursor position
   */
  const storeCursorPosition = (position: number | null) => {
    cursorPositionRef.current = position;
  };

  return {
    scheduleCursorUpdate,
    storeCursorPosition,
    cursorPosition: cursorPositionRef,
  };
};

/**
 * Checks if input operation deleted a space
 */
export const isSpaceDeleted = (
  previousValue: string,
  currentValue: string,
  cursorPosition: number | null
): boolean => {
  if (
    !cursorPosition ||
    cursorPosition <= 0 ||
    previousValue.length <= cursorPosition
  ) {
    return false;
  }

  // Check if length decreased by 1 character
  const lengthDecreased = previousValue.length === currentValue.length + 1;

  // Check if the character at cursor position in previous value was a space
  const wasSpaceAtCursor = previousValue[cursorPosition] === ' ';
  const wasSpaceBeforeCursor = previousValue[cursorPosition - 1] === ' ';

  return lengthDecreased && (wasSpaceAtCursor || wasSpaceBeforeCursor);
};
